import { motion } from "framer-motion";
import {
  useProfile,
  useReservations,
  usePolls,
} from "@/lib/api";
import { UserAPI } from "@shared/types/api";
import { eventTimeToDaysjs } from "@/utils/dateUtils";
import { ReservationCard } from "../components/ReservationCard";
import EmailVerificationBanner from "../components/EmailVerificationBanner";
import InstallAppBanner from "../components/InstallAppBanner";
import { usePushPrimer } from "../lib/usePushPermissionAsk";
import PushPrimer from "../components/PushPrimer";
import { PendingInviteBanner } from "../components/PendingInviteBanner";
import { PollCard } from "../components/PollCard";
import BookingSection from "../components/BookingSection";
import { CarouselRail } from "@/components/EventCarouselRail";
import { useMyLikedEvents } from "@/lib/api/useEventsList";
import { KikiCarousel } from "@/components/KikiCarousel";
import { useMyKikis } from "@/lib/api/useEventBuddies";
import { useIsSignedIn } from "@/lib/platform/AuthProvider";
import dayjs from "dayjs";
import { useT } from "@/lib/i18n";
import ImageHeader from "../components/ux/ImageHeader";
import reservationsImg from "@/assets/images/img_bar.webp";
import MobileLogoHeader from "../components/ux/MobileLogoHeader";
import { Loader } from '@/components/Loader';

const filterActiveReservations = (r: UserAPI.Reservation) => {
  if (r.status !== "active" && r.status !== "cant_be_seated") {
    return false;
  }
  if (r.feedbackCompleted) {
    return eventTimeToDaysjs(r.eventTime).add(7, 'days').isAfter(dayjs())
  }
  return eventTimeToDaysjs(r.eventTime).add(14, 'days').isAfter(dayjs());
}
export default function Dashboard() {
  const { t } = useT();
  const { isLoading: profileLoading } = useProfile();
  const { data: reservationsData, isLoading: reservationsLoading, refetch: refetchReservations } =
    useReservations();

  // The native app's notification-permission ask: anyone holding a seat is
  // owed the pre-dinner reminders, and this is the screen they all land on.
  // Soft prompt first — see usePushPrimer for why this path in particular
  // cannot afford to raise the system sheet cold.
  const pushPrimer = usePushPrimer();
  const { data: pollsData } = usePolls();
  const { data: likedEventsData } = useMyLikedEvents();
  const likedEvents = likedEventsData?.success ? likedEventsData.data ?? [] : [];
  const { data: myKikisData } = useMyKikis();
  const myKikis = myKikisData?.success ? myKikisData.data ?? [] : [];
  const isSignedIn = useIsSignedIn();

  const userReservations = reservationsData?.data || [];
  const isWithin48h = (r: UserAPI.Reservation) => {
    const eventTime = eventTimeToDaysjs(r.eventTime);
    const now = dayjs();
    return eventTime.isAfter(now) && eventTime.diff(now, 'hour') <= 48;
  };
  const activeReservations = userReservations
    .filter(filterActiveReservations)
    .sort((a, b) => {
      const aSoon = isWithin48h(a);
      const bSoon = isWithin48h(b);
      if (aSoon !== bSoon) return aSoon ? -1 : 1;
      return a.eventTime.date.localeCompare(b.eventTime.date);
    });

  if (profileLoading) {
    return (
      <div className="flex items-center justify-center pt-16">
        <Loader />
      </div>
    );
  }

  return (
    <div className="pt-4 pb-24 md:pt-8 px-4">
      {/* Soft ask for notification permission, before the system sheet. Sits
          above the page rather than in it — it renders nothing until the gate
          in `usePushPrimer` opens. */}
      <PushPrimer primer={pushPrimer} />

      <div className="max-w-6xl mx-auto">
        {/* Email Verification Banner */}
        <EmailVerificationBanner />

        <MobileLogoHeader />


        {/* Pending +1 invite — surfaces a stashed invite code as a banner so
            users can accept on their own schedule instead of being redirected. */}
        <PendingInviteBanner />

        {/* Permanent app-install nudge while a booking is upcoming and the
            user has no app device — reminders and the table chat ride on push. */}
        <InstallAppBanner />

        {/* Upcoming Events */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="my-8"
        >
          <BookingSection maxEvents={4} showViewAll />
        </motion.div>

        {/* My Events — the events the user has liked, as a horizontal rail */}
        {likedEvents.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
            className="my-8 -mx-4"
          >
            <CarouselRail
              carousel={{
                id: "my-liked-events",
                title: "My Events",
                largeCards: false,
                items: likedEvents,
              }}
            />
          </motion.div>
        )}

        {/* My Kikis — kikis the user hosts or has joined. Reuses the events
            index KikiCarousel, sans the "start your own" picker. */}
        {myKikis.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.45 }}
            className="my-8 -mx-4"
          >
            <KikiCarousel
              kikis={myKikis}
              isSignedIn={isSignedIn}
              title="My Kikis"
              subtitle={null}
              showCreate={false}
            />
          </motion.div>
        )}

        {/* Active Polls */}
        {pollsData?.data && pollsData.data.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5 }}
            className="my-8"
          >
            <h2 className="text-2xl font-bold mb-6">{t('dashboard.communityPolls')}</h2>
            <div className="space-y-4">
              {pollsData.data.map((poll) => (
                <PollCard key={poll.id} poll={poll} />
              ))}
            </div>
          </motion.div>
        )}

        {/* My Reservations */}
        {reservationsLoading ? (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.7 }}
            className="my-8"
          >
            <ImageHeader
              title={t('dashboard.myReservations')}
              imageSrc={reservationsImg}
              className="mb-3"
            />
            <div className="card">
              <div className="flex items-center justify-center py-12">
                <Loader />
              </div>
            </div>
          </motion.div>
        ) : (
          activeReservations.length > 0 && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.7 }}
              className="my-8"
            >
              <ImageHeader
              title={t('dashboard.myReservations')}
              imageSrc={reservationsImg}
              className="mb-3"
            />
              <div className="space-y-4">
                {activeReservations.map((reservation: UserAPI.Reservation) => (
                  <ReservationCard
                    key={reservation.id}
                    reservation={reservation}
                    onCancelSuccess={refetchReservations}
                  />
                ))}
              </div>
            </motion.div>
          )
        )}
      </div>
    </div>
  );
}
